/**
 * Die Führungsanzeige: oben das nächste Manöver, unten Tempo, Tempolimit und
 * was noch bleibt. Sie legt sich über die Karte und schaltet die Seitenleiste
 * beiseite - beim Fahren zählt ein Blick, nicht eine Liste.
 */
import { createNavigation, strecke, dauer } from '../data/navigation.js';
import { routeVehicle, fetchSpeedLimits } from '../data/routing.js';

/** Valhalla-Manövertypen -> Pfeil. Nur die Richtung, nicht jede Feinheit. */
const PFEILE = {
  1: '↑', 2: '↑', 3: '↑', 4: '◆', 5: '◆', 6: '◆',
  9: '↗', 10: '→', 11: '↘', 12: '↱', 13: '↰',
  14: '↙', 15: '←', 16: '↖', 17: '↑', 18: '↑',
  19: '↗', 20: '↖', 21: '↗', 22: '↑', 23: '↗', 24: '↖',
  25: '↱', 26: '⟳', 27: '⤷', 37: '↑'
};

const SPRACHE = 'de-CH';

export function createNavOverlay({ map, onStart, onEnd }) {
  let nav = null;
  let root = null;
  let ziel = null;
  let modus = 'auto';
  let stumm = false;
  let letzteAnsage = '';
  const el = {};

  function sprich(text) {
    if (stumm || !text || text === letzteAnsage) return;
    letzteAnsage = text;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = SPRACHE;
      u.rate = 1.05;
      speechSynthesis.cancel();
      speechSynthesis.speak(u);
    } catch { /* ohne Stimme bleibt die Anzeige */ }
  }

  function bauen() {
    root = document.createElement('div');
    root.className = 'nav';

    const oben = document.createElement('div');
    oben.className = 'nav__banner';
    el.pfeil = document.createElement('div');
    el.pfeil.className = 'nav__arrow';
    const textbox = document.createElement('div');
    textbox.className = 'nav__texts';
    el.distanz = document.createElement('div');
    el.distanz.className = 'nav__dist';
    el.anweisung = document.createElement('div');
    el.anweisung.className = 'nav__instr';
    textbox.append(el.distanz, el.anweisung);
    oben.append(el.pfeil, textbox);

    const unten = document.createElement('div');
    unten.className = 'nav__bar';

    el.tempo = document.createElement('div');
    el.tempo.className = 'nav__speed';
    el.tempo.innerHTML = '<strong>–</strong><span>km/h</span>';

    el.limit = document.createElement('div');
    el.limit.className = 'nav__limit';
    el.limit.hidden = true;

    el.rest = document.createElement('div');
    el.rest.className = 'nav__rest';

    const knoepfe = document.createElement('div');
    knoepfe.className = 'nav__btns';
    el.ton = document.createElement('button');
    el.ton.type = 'button';
    el.ton.className = 'nav__btn';
    el.ton.textContent = 'Ton aus';
    el.ton.addEventListener('click', () => {
      stumm = !stumm;
      el.ton.textContent = stumm ? 'Ton an' : 'Ton aus';
      if (stumm) speechSynthesis.cancel();
    });
    const schluss = document.createElement('button');
    schluss.type = 'button';
    schluss.className = 'nav__btn nav__btn--end';
    schluss.textContent = 'Beenden';
    schluss.addEventListener('click', () => beenden());
    knoepfe.append(el.ton, schluss);

    unten.append(el.tempo, el.limit, el.rest, knoepfe);
    root.append(oben, unten);
    document.body.append(root);
  }

  function zeigen(d) {
    el.pfeil.textContent = PFEILE[d.manoever.typ] || '↑';
    el.distanz.textContent = strecke(d.bisManoever);
    el.anweisung.textContent = d.manoever.text;

    const kmh = d.tempoKmh;
    el.tempo.querySelector('strong').textContent =
      kmh === null ? '–' : String(Math.round(kmh));

    if (d.limit) {
      el.limit.hidden = false;
      el.limit.textContent = String(d.limit);
      // Nur einfärben, wenn die Messung auch taugt - unter 3 km/h ist sie Rauschen.
      const zuSchnell = kmh !== null && kmh > 3 && kmh > d.limit + 5;
      el.limit.classList.toggle('is-over', zuSchnell);
    } else {
      el.limit.hidden = true;
    }

    const ankunft = new Date(Date.now() + d.restSekunden * 1000);
    el.rest.textContent =
      `${strecke(d.uebrig)} · ${dauer(d.restSekunden)} · an ${String(ankunft.getHours()).padStart(2, '0')}:${String(ankunft.getMinutes()).padStart(2, '0')}`;

    // Karte mitführen: Blick in Fahrtrichtung, geneigt.
    map.easeTo({
      center: [d.position.lng, d.position.lat],
      bearing: d.kurs ?? map.getBearing(),
      pitch: 55,
      zoom: Math.max(16, map.getZoom()),
      duration: 900,
      essential: true
    });

    if (d.amZiel) {
      sprich('Sie haben Ihr Ziel erreicht.');
      setTimeout(beenden, 2500);
    }
  }

  async function neuBerechnen(von) {
    if (!ziel) return;
    sprich('Route wird neu berechnet.');
    try {
      const res = await routeVehicle({ from: von, to: ziel, costing: modus });
      const limits = await fetchSpeedLimits({ shape: res.shape, costing: modus }).catch(() => []);
      zeichneRoute(res.coordinates);
      nav.ersetzen({ ...res, limits });
    } catch {
      sprich('Neue Route nicht möglich.');
    }
  }

  function zeichneRoute(coordinates) {
    const data = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }]
    };
    if (map.getSource('route')) map.getSource('route').setData(data);
  }

  async function starten({ route, to, costing }) {
    ziel = to;
    modus = costing || 'auto';
    if (!route?.maneuvers?.length) return;

    if (!root) bauen();
    root.hidden = false;
    document.body.classList.add('is-navigating');
    onStart?.();

    // Tempolimits nachladen - die Fuehrung startet auch ohne sie.
    const limits = await fetchSpeedLimits({ shape: route.shape, costing: modus }).catch(() => []);

    nav = createNavigation({
      onUpdate: zeigen,
      onAnnounce: sprich,
      onOffRoute: neuBerechnen
    });
    nav.start({ ...route, limits });
    sprich(route.maneuvers[0]?.ansageJetzt || 'Navigation gestartet.');
  }

  function beenden() {
    nav?.stop();
    nav = null;
    speechSynthesis.cancel();
    if (root) root.hidden = true;
    document.body.classList.remove('is-navigating');
    map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
    onEnd?.();
  }

  return { starten, beenden, laeuft: () => Boolean(nav) };
}
